import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import logger from '../utils/logger';
import { detectBrowserTimezone, saveUserTimezone } from '../utils/dateUtils';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (err) {
        logger.error('Error parsing stored user data', { error: err, storedUser });
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      // Clear ALL localStorage first to prevent data leakage between users
      localStorage.clear();

      const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/login`, {
        email,
        password
      });

      const { access_token, user: userData } = response.data;

      // Save to localStorage
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));

      // Save user's timezone to localStorage
      if (userData.timezone) {
        saveUserTimezone(userData.timezone);
        logger.info('Saved user timezone on login', { timezone: userData.timezone });
      } else {
        // Fallback: detect browser timezone if user doesn't have one set
        const browserTimezone = detectBrowserTimezone();
        saveUserTimezone(browserTimezone);
        logger.info('Saved browser timezone on login (user had no timezone)', { timezone: browserTimezone });
      }

      // Update state
      setUser(userData);

      return { success: true };
    } catch (error) {
      logger.error('Login failed', { error, email });
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  };

  const register = async (email, password, fullName, verificationToken) => {
    try {
      // Clear ALL localStorage first to prevent data leakage between users
      localStorage.clear();

      // Detect browser timezone for new user registration
      const browserTimezone = detectBrowserTimezone();
      logger.info('Detected browser timezone for registration', { timezone: browserTimezone });

      const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/register`, {
        email,
        password,
        full_name: fullName,
        verification_token: verificationToken,
        timezone: browserTimezone  // Send detected timezone to backend
      });

      const { access_token, user: userData } = response.data;

      // Save to localStorage
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));

      // Save user's timezone (use what backend returned, or fallback to detected)
      const userTimezone = userData.timezone || browserTimezone;
      saveUserTimezone(userTimezone);
      logger.info('Saved user timezone on registration', { timezone: userTimezone });

      // Update state
      setUser(userData);

      return { success: true };
    } catch (error) {
      logger.error('Registration failed', { error, email });
      throw new Error(error.response?.data?.error || 'Registration failed');
    }
  };

  const logout = () => {
    // Only clear authentication-related data, preserve user-specific valuation data
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userTimezone');  // Clear timezone on logout
    setUser(null);
  };

  const updateUser = (userData) => {
    // If userData is a complete user object (has id), replace entirely
    // Otherwise merge with existing user
    const updatedUser = userData.id ? userData : { ...user, ...userData };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const getToken = () => {
    return localStorage.getItem('token');
  };

  const value = {
    user,
    token: getToken(),
    login,
    register,
    logout,
    updateUser,
    loading
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
